import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/SimpleToast";
import { mailingApi } from "@/lib/api";
import { collectUrls, findUnknownPlaceholders } from "./emailBlocks";
import { Monitor, Smartphone, CheckCircle2, AlertTriangle, Loader2, Link2, ShieldCheck } from "lucide-react";

export const EmailPreviewDialog = ({ open, onOpenChange, html, blocks = [], subject = "" }) => {
  const toast = useToast();
  const [device, setDevice] = useState("desktop");
  const [linkResults, setLinkResults] = useState(null);
  const [checking, setChecking] = useState(false);

  const unknownPlaceholders = findUnknownPlaceholders(blocks, subject);
  const urls = collectUrls(blocks);

  useEffect(() => { if (open) { setLinkResults(null); setDevice("desktop"); } }, [open]);

  const runLinkCheck = async () => {
    if (urls.length === 0) { toast.info?.("Keine Links zum Prüfen"); setLinkResults([]); return; }
    setChecking(true);
    try {
      const res = await mailingApi.checkLinks(urls.map((u) => u.url));
      setLinkResults(res.data.results || []);
    } catch (e) {
      toast.error("Linkprüfung fehlgeschlagen");
    } finally {
      setChecking(false);
    }
  };

  const brokenLinks = (linkResults || []).filter((r) => !r.ok);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] flex flex-col" data-testid="email-preview-dialog">
        <DialogHeader>
          <DialogTitle>Vorschau &amp; Qualitäts-Check</DialogTitle>
          <DialogDescription>So sieht die Rundmail für Ihre Empfänger aus (mit Beispieldaten).</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col lg:flex-row gap-4 min-h-0 flex-1 overflow-hidden">
          {/* Vorschau */}
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40 w-fit mb-2">
              <button type="button" onClick={() => setDevice("desktop")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${device === "desktop" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
                data-testid="preview-device-desktop">
                <Monitor className="w-3.5 h-3.5" /> Desktop
              </button>
              <button type="button" onClick={() => setDevice("mobile")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${device === "mobile" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
                data-testid="preview-device-mobile">
                <Smartphone className="w-3.5 h-3.5" /> Mobil
              </button>
            </div>
            <div className="flex-1 min-h-0 rounded-lg border overflow-auto bg-[#eef1f4] flex justify-center p-3">
              <iframe
                title="E-Mail-Vorschau"
                srcDoc={html}
                sandbox=""
                className="bg-white border shadow-sm transition-all"
                style={{ width: device === "mobile" ? "375px" : "640px", height: "60vh", flexShrink: 0 }}
                data-testid="email-preview-iframe"
              />
            </div>
          </div>

          {/* QA-Panel */}
          <div className="lg:w-72 shrink-0 space-y-3 overflow-y-auto" data-testid="qa-panel">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <ShieldCheck className="w-4 h-4 text-primary" /> Qualitäts-Check
            </div>

            {/* Platzhalter */}
            <div className="rounded-lg border p-3 space-y-1.5" data-testid="qa-placeholders">
              <div className="flex items-center gap-2 text-sm font-medium">
                {unknownPlaceholders.length === 0
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                Platzhalter
              </div>
              {unknownPlaceholders.length === 0 ? (
                <p className="text-xs text-muted-foreground">Alle Platzhalter sind gültig.</p>
              ) : (
                <p className="text-xs text-amber-700">
                  Unbekannt (bleibt im Text stehen): {unknownPlaceholders.map((p) => `{{${p}}}`).join(", ")}
                </p>
              )}
            </div>

            {/* Links */}
            <div className="rounded-lg border p-3 space-y-2" data-testid="qa-links">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Link2 className="w-4 h-4 text-primary" /> Links ({urls.length})
              </div>
              {linkResults === null ? (
                <Button type="button" variant="outline" size="sm" className="w-full h-8" onClick={runLinkCheck} disabled={checking} data-testid="qa-check-links-btn">
                  {checking ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                  Links prüfen
                </Button>
              ) : brokenLinks.length === 0 ? (
                <p className="text-xs text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Alle Links erreichbar.</p>
              ) : (
                <div className="space-y-1">
                  {brokenLinks.map((r, i) => (
                    <p key={i} className="text-xs text-destructive break-all" data-testid={`qa-broken-link-${i}`}>
                      ⚠ {r.url} — {r.error || (r.status ? `HTTP ${r.status}` : "Nicht erreichbar")}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="pt-2 border-t">
          <Button type="button" onClick={() => onOpenChange(false)}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
